import { Wallet, TrendingUp, DollarSign } from "lucide-react";

const AdvancementSection = ({
  t,
  styles,
  theme,
  language,
  advancementStats,
  advancementRequests,
  advancementLimits,
  isLoadingLimits,
  urgencyLevels,
  formatCurrency,
  formatDate,
  getStatusColor,
  getStatusIcon,
  getUrgencyColor,
  setShowAdvancementRulesModal,
  setAlertModal,
}) => {
  return (
    <>
      {/* Advancement Statistics */}
      {advancementStats && (
        <div id="advancement-section" style={styles.card1}>
          <h3 style={styles.cardTitle}>
            <Wallet size={28} />
            {t.solfaOverview}
          </h3>

          <div style={styles.statsGrid}>
            {[
              { value: advancementStats.totalRequests, label: t.totalRequests },
              { value: formatCurrency(advancementStats.totalRequested), label: t.totalRequested },
              { value: advancementStats.activeAdvances, label: t.activeAdvances },
              { value: formatCurrency(advancementStats.outstandingBalance), label: t.outstandingBalance },
            ].map((stat, i) => (
              <div
                key={i}
                style={styles.statCard}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow =
                    theme === "dark"
                      ? "0 20px 50px rgba(0, 0, 0, 0.5), 0 10px 25px rgba(59, 130, 246, 0.3)"
                      : "0 20px 50px rgba(0, 0, 0, 0.12), 0 10px 25px rgba(59, 130, 246, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    theme === "dark"
                      ? "0 15px 40px rgba(0, 0, 0, 0.4), 0 8px 20px rgba(59, 130, 246, 0.2)"
                      : "0 15px 40px rgba(0, 0, 0, 0.08), 0 8px 20px rgba(59, 130, 246, 0.1)";
                }}
              >
                <div style={styles.statValue}>{stat.value}</div>
                <div style={styles.statLabel}>{stat.label}</div>
              </div>
            ))}
          </div>

          <button
            style={{
              ...styles.primaryButton,
              opacity: isLoadingLimits ? 0.5 : !advancementLimits?.enabled ? 0.7 : 1,
              cursor: isLoadingLimits ? "wait" : "pointer",
            }}
            onClick={() => {
              if (isLoadingLimits) {
                setAlertModal({
                  open: true,
                  title: language === "ar" ? "جاري التحميل" : "Loading",
                  message: language === "ar" ? "جاري تحميل قواعد السلفة..." : "Loading advancement rules...",
                  variant: "info",
                  showCancel: false,
                  onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
                });
                return;
              }
              if (!advancementLimits?.enabled) {
                setAlertModal({
                  open: true,
                  title: language === "ar" ? "تنبيه" : "Notice",
                  message:
                    advancementLimits?.message ||
                    (language === "ar" ? "السلف المالية غير متاحة حالياً" : "Advancements are currently disabled"),
                  variant: "info",
                  showCancel: false,
                  onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
                });
                return;
              }
              setShowAdvancementRulesModal(true);
            }}
          >
            <DollarSign size={20} />
            {isLoadingLimits
              ? language === "ar" ? "جاري التحميل..." : "Loading..."
              : t.requestAdvancement}
          </button>
        </div>
      )}

      {/* Advancement Requests List */}
      <div style={styles.card1}>
        <h3 style={styles.cardTitle}>
          <TrendingUp size={28} />
          {t.mySolfaRequests} ({advancementRequests.length})
        </h3>

        {advancementRequests.length === 0 ? (
          <div style={styles.emptyState}>
            <DollarSign size={80} style={styles.emptyStateIcon} />
            <p style={styles.emptyStateText}>{t.noSolfaYet}</p>
          </div>
        ) : (
          <div>
            {advancementRequests.map((request) => {
              const StatusIcon = getStatusIcon(request.status);
              return (
                <div
                  key={request.id}
                  style={styles.requestItem}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      theme === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.05)";
                    e.currentTarget.style.transform = "translateX(6px)";
                    e.currentTarget.style.boxShadow =
                      theme === "dark"
                        ? "0 12px 32px rgba(0, 0, 0, 0.4)"
                        : "0 12px 32px rgba(0, 0, 0, 0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      theme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)";
                    e.currentTarget.style.transform = "translateX(0)";
                    e.currentTarget.style.boxShadow =
                      theme === "dark"
                        ? "0 8px 24px rgba(0, 0, 0, 0.3)"
                        : "0 8px 24px rgba(0, 0, 0, 0.06)";
                  }}
                >
                  <div style={styles.requestIcon(request.status)}>
                    <StatusIcon size={28} color={getStatusColor(request.status)} />
                  </div>

                  <div style={styles.requestContent}>
                    <h4 style={styles.requestTitle}>{formatCurrency(request.requestedAmount)}</h4>
                    <div style={styles.requestMeta}>
                      <span>{formatDate(request.requestedDate)}</span>
                      {request.numberOfInstallments && (
                        <>
                          <span>•</span>
                          <span>{request.numberOfInstallments} {t.installments}</span>
                        </>
                      )}
                      {request.remainingBalance > 0 && request.status === "disbursed" && (
                        <>
                          <span>•</span>
                          <span>{t.balance} {formatCurrency(request.remainingBalance)}</span>
                        </>
                      )}
                    </div>
                    {request.reason && (
                      <div
                        style={{
                          fontSize: "14px",
                          color: theme === "dark" ? "rgba(255, 255, 255, 0.65)" : "rgba(30, 41, 59, 0.65)",
                          marginTop: "8px",
                          lineHeight: 1.5,
                        }}
                      >
                        {request.reason}
                      </div>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                    <span style={styles.urgencyBadge(request.urgency, getUrgencyColor)}>
                      {urgencyLevels.find((l) => l.id === request.urgency)?.name || request.urgency}
                    </span>
                    <span style={styles.statusBadge(request.status)}>{request.status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default AdvancementSection;